import { useCallback, useEffect, useState } from 'react'
import { api, type KlaerungAnsicht, type KorrekturVorschlag, type PersonUebersicht, type PruefFrage } from '../api'
import { link } from '../navigation'
import { Klaerungen } from './Klaerungen'
import { KorrekturVorschlaege } from './Korrektur'
import { PersonDetail } from './PersonDetail'
import { PruefFragen } from './PruefFragen'
import { UeberMich } from './UeberMich'

export function Personen({ pfad, onRueckfragenGeaendert }: { pfad: string[]; onRueckfragenGeaendert: () => void }) {
  if (pfad[0] === 'ich') return <UeberMich />
  const id = Number(pfad[0])
  if (id) return <PersonDetail key={id} id={id} />
  return <PersonenListe onRueckfragenGeaendert={onRueckfragenGeaendert} />
}

function PersonenListe({ onRueckfragenGeaendert }: { onRueckfragenGeaendert: () => void }) {
  const [suche, setSuche] = useState('')
  const [personen, setPersonen] = useState<PersonUebersicht[] | null>(null)
  const [klaerungen, setKlaerungen] = useState<KlaerungAnsicht[]>([])
  const [korrekturen, setKorrekturen] = useState<KorrekturVorschlag[]>([])
  const [pruefFragen, setPruefFragen] = useState<PruefFrage[]>([])
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(
    (s: string) =>
      api
        .personen(s)
        .then((p) => {
          setPersonen(p)
          setFehler(null)
        })
        .catch((e) => setFehler((e as Error).message)),
    [],
  )

  const klaerungenLaden = useCallback(() => {
    api.klaerungen().then(setKlaerungen).catch(() => {})
    api.korrekturVorschlaege().then(setKorrekturen).catch(() => {})
    api.pruefFragen().then(setPruefFragen).catch(() => {})
  }, [])

  useEffect(() => {
    klaerungenLaden()
  }, [klaerungenLaden])

  // Suche leicht verzoegert, damit nicht jeder Tastendruck eine Anfrage ausloest
  useEffect(() => {
    const t = setTimeout(() => laden(suche), 200)
    return () => clearTimeout(t)
  }, [suche, laden])

  const geklaert = () => {
    klaerungenLaden()
    laden(suche)
    onRueckfragenGeaendert()
  }

  return (
    <div className="personen">
      {pruefFragen.length > 0 && <PruefFragen fragen={pruefFragen} onGeklaert={geklaert} />}
      {korrekturen.length > 0 && <KorrekturVorschlaege vorschlaege={korrekturen} onGeklaert={geklaert} />}
      {klaerungen.length > 0 && <Klaerungen klaerungen={klaerungen} onGeklaert={geklaert} />}

      <a href={link.ueberMich} className="karte person-zeile">
        <strong>Über mich</strong>
        <span className="leise klein">Was deine Notizen über dich selbst sagen</span>
      </a>

      <input type="search" placeholder="Suchen: Name, Beschreibung, Fakten …" value={suche} onChange={(e) => setSuche(e.target.value)} />
      {fehler && <p className="fehler">{fehler}</p>}

      {personen?.length === 0 && (
        <div className="platzhalter">
          {suche ? (
            <p>Niemand gefunden.</p>
          ) : (
            <>
              <p>Noch keine Personen.</p>
              <p className="leise">Sobald du in einer Notiz von jemandem erzählst, erscheint die Person hier.</p>
            </>
          )}
        </div>
      )}

      <ul className="liste">
        {personen?.map((p) => (
          <li key={p.id}>
            <a href={link.person(p.id)} className="karte person-zeile">
              <strong>{p.name}</strong>
              {p.aliase.length > 0 && <span className="leise"> ({p.aliase.join(', ')})</span>}
              {p.beschreibung && <span className="beschreibung">{p.beschreibung}</span>}
              <span className="leise klein">
                {p.anzahlFakten === 1 ? '1 Fakt' : `${p.anzahlFakten} Fakten`}
                {p.zuletzt && ` · zuletzt ${new Date(p.zuletzt).toLocaleDateString('de-DE')}`}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
